/**
 * §P.6 fase 2 · El muro del club, tal como lo ven las familias.
 *
 * ## «El maestro no puede corregir lo que no puede mirar»
 *
 * Es la mitad del valor de esta pantalla, y está en el contrato: la fila que se
 * enseña aquí es **la misma** que lee la app de familias. Sin esto, el muro es una
 * caja negra que se llena sola y que nadie del club ha visto nunca.
 *
 * ## Retirar es una fila; no poder retirarla es una llamada
 *
 * `DELETE /feed/{id}` se añadió por eso: «el muro se llena solo, y cuando el maestro
 * corrige una lista mal pasada la entrada ya está publicada en la app de 120
 * familias». Así que el botón está en cada entrada y no escondido.
 *
 * ## `system` frente a `manual`
 *
 * Lo que se generó solo y lo que alguien escribió. El filtro existe porque «es la
 * distinción que permite ver, mirando los datos, un club que **usa** el producto
 * frente a uno que escribe posts» — y al club le sirve para lo de todos los días:
 * buscar la entrada mala entre cuarenta automáticas.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fechaYHora } from '@/shared/ui/formato';
import {
  ALCANCES_DEL_MURO,
  etiquetaDeTipoDeEntrada,
  type EntradaDelMuro,
} from '@/club/avisos/comunicacion';
import { enlaceSeguro } from '@/shared/ui/enlaceSeguro';

const TOPE = 100;

export function Muro({
  club,
  puedeRetirar,
  alAbrirAlumno,
}: {
  club: string;
  puedeRetirar: boolean;
  alAbrirAlumno: (alumnoId: string) => void;
}) {
  const cache = useQueryClient();
  const [origen, setOrigen] = useState<'' | 'system' | 'manual'>('');
  const [retirando, setRetirando] = useState<EntradaDelMuro | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);

  const muro = useQuery({
    queryKey: ['muro', club, origen],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).feed.get({
        query: { limit: TOPE, ...(origen === '' ? {} : { source: origen }) },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as EntradaDelMuro[];
    },
  });

  const retirar = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1.clubs({ club }).feed({ feedItemId: id }).delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setRetirando(null);
      void cache.invalidateQueries({ queryKey: ['muro', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (retirando) {
    return (
      <div className="hueco hueco-confirmar">
        <h3>¿Retirar esta entrada del muro?</h3>

        <ul className="lista-clara">
          <li>
            Desaparece de la app de las familias.{' '}
            <strong>Quien ya la haya visto, la vio</strong>: retirarla no
            deshace lo que se leyó.
          </li>
          {retirando.source === 'system' && (
            <li>
              La generó Kiap sola. Si el dato de origen sigue mal —una lista
              pasada con un error—, <strong>conviene corregir el dato</strong>:
              si no, la próxima entrada volverá a decir lo mismo.
            </li>
          )}
        </ul>

        {problema && <PanelError problema={problema} />}

        <div className="hueco-acciones">
          <button
            type="button"
            className="btn-peligro"
            disabled={retirar.isPending}
            onClick={() => retirar.mutate(retirando.id)}
          >
            {retirar.isPending ? 'Retirando…' : 'Sí, retirarla'}
          </button>
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setRetirando(null);
              setProblema(null);
            }}
          >
            Dejarlo
          </button>
        </div>
      </div>
    );
  }

  return (
    <section>
      <h1>El muro</h1>

      <p className="tenue">
        Lo que ven las familias en su app, <strong>tal cual lo ven</strong>. Se
        llena solo con lo que pasa en el club; aquí sirve para revisarlo y
        retirar lo que esté mal.
      </p>

      <div className="filtros">
        <label>
          Qué enseñar
          <select
            value={origen}
            onChange={(e) =>
              setOrigen(e.target.value as '' | 'system' | 'manual')
            }
          >
            <option value="">Todo</option>
            <option value="system">Solo lo automático</option>
            <option value="manual">Solo lo que ha escrito alguien</option>
          </select>
        </label>
      </div>

      {muro.isPending && <Cargando filas={5} etiqueta="Cargando el muro" />}

      {muro.isError && (
        <PanelError
          problema={aProblema(muro.error)}
          reintentar={() => void muro.refetch()}
        />
      )}

      {problema && <PanelError problema={problema} />}

      {muro.data?.length === 0 && (
        <Vacio
          titulo="El muro está vacío"
          explicacion="Se llena solo: un grado nuevo, una racha de asistencia, un cumpleaños. Cuando el club empiece a pasar lista y a graduar, esto se llena sin que nadie escriba nada."
        />
      )}

      {muro.data && muro.data.length > 0 && (
        <ul className="muro">
          {muro.data.map((x) => {
            const alcance = ALCANCES_DEL_MURO[x.visibility];

            return (
              <li key={x.id}>
                <div className="aviso-cabeza">
                  <strong>{x.title ?? etiquetaDeTipoDeEntrada(x.type)}</strong>
                  {/*
                    Automática o escrita: la distinción más informativa del muro, y
                    la que dice si hay que corregir el dato o el texto.
                  */}
                  <span
                    className={`pastilla ${
                      x.source === 'manual' ? 'pastilla-info' : 'pastilla-left'
                    }`}
                  >
                    {x.source === 'manual' ? 'Escrita' : 'Automática'}
                  </span>
                  {alcance && (
                    <span className="pastilla" title={alcance.explicacion}>
                      {alcance.etiqueta}
                    </span>
                  )}
                </div>

                {x.body && <p className="aviso-cuerpo">{x.body}</p>}

                {x.external_url && (
                  <p className="tenue">
                    <a
                      href={enlaceSeguro(x.external_url)}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      El enlace que ven las familias
                    </a>
                  </p>
                )}

                <div className="aviso-pie">
                  <span className="tenue">
                    {etiquetaDeTipoDeEntrada(x.type)}
                    {x.student_name ? ' · ' : ''}
                    {x.student_id && x.student_name && (
                      <button
                        type="button"
                        className="enlace-fila"
                        onClick={() => alAbrirAlumno(x.student_id!)}
                      >
                        {x.student_name}
                      </button>
                    )}
                    {x.group_name ? ` · ${x.group_name}` : ''}
                    {' · '}
                    {fechaYHora(x.occurred_at)}
                    {x.created_by_name ? ` · ${x.created_by_name}` : ''}
                  </span>

                  {puedeRetirar && (
                    <button
                      type="button"
                      className="btn-plano"
                      onClick={() => {
                        setRetirando(x);
                        setProblema(null);
                      }}
                    >
                      Retirar
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {muro.data?.length === TOPE && (
        <p className="tenue">Se enseñan las {TOPE} más recientes.</p>
      )}
    </section>
  );
}
